import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	BrandTeam,
	Event,
	initialBrandTeam,
	initialEvent,
	initialLeaderboard,
	initialLeague,
	initialLeagueWithSportsAndSeries,
	initialRider,
	initialSeason,
	initialSeries,
	initialSport,
	initialTeamComplete,
	Leaderboard,
	League,
	LeagueWithSportsAndSeries,
	Result,
	Rider,
	RiderWithSports,
	Season,
	Series,
	Sport,
	TeamComplete,
	Video,
} from "../../types/model";

interface FantasyState {
	sports: Sport[];
	series: Series[];
	events: Event[];
	seasons: Season[];
	futureEvents: Event[];
	videos: Video[];
	riders: RiderWithSports[];
	results: Result[];
	featuredRider: Rider;
	featuredBrandTeam: BrandTeam;
	featuredRiderDescription: string;
	featuredBrandTeamDescription: string;
	brandTeams: BrandTeam[];
	selectedSport: Sport;
	selectedSeries: Series;
	selectedSeason: Season;
	selectedEvent: Event;
	selectedLeague: LeagueWithSportsAndSeries;
	selectedLeaderBoard: Leaderboard;
	selectedTeam: TeamComplete;
	eventLoading: boolean;
	leaderboardLoading: boolean;
	teamLoading: boolean;
}

const toId = (value: unknown): string => {
	if (value === undefined || value === null) {
		return "";
	}
	if (typeof value === "object") {
		const maybeRecord = value as { _id?: unknown; id?: unknown; $oid?: unknown };
		return String(maybeRecord._id ?? maybeRecord.id ?? maybeRecord.$oid ?? "");
	}
	return String(value);
};

const getFutureEventsForSeason = (
	events: Event[],
	seasonId?: string,
): Event[] => {
	return events
		.filter((event) => {
			const eventDate = new Date(event.date);
			const isFuture =
				eventDate > new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);
			const matchesSeason = seasonId ? toId(event.season) === seasonId : true;
			return isFuture && matchesSeason;
		})
		.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

const setFutureEventsForSelection = (state: FantasyState) => {
	const seasonId = toId(state.selectedSeason?._id);
	const futureEvents = getFutureEventsForSeason(state.events, seasonId);
	state.futureEvents = futureEvents.filter((event) => {
		const matchesSport = state.selectedSport?._id
			? toId(event.sport?._id) === toId(state.selectedSport._id)
			: true;
		const matchesSeries = state.selectedSeries?._id
			? toId(event.series?._id) === toId(state.selectedSeries._id)
			: true;
		return matchesSport && matchesSeries;
	});
};

const applySelectedEvent = (state: FantasyState, event?: Event) => {
	if (!event?._id) {
		state.selectedEvent = initialEvent;
		state.selectedSport = initialSport;
		state.selectedSeries = initialSeries;
		setFutureEventsForSelection(state);
		return;
	}

	state.selectedEvent = event;
	state.selectedSport = event.sport;
	state.selectedSeries = event.series;
	setFutureEventsForSelection(state);
};

const chooseEventForSport = (state: FantasyState, sport: Sport): Event | undefined => {
	const seasonId = toId(state.selectedSeason?._id);
	const futureEvents = getFutureEventsForSeason(state.events, seasonId);
	const sportEvents = futureEvents.filter(
		(event) => toId(event.sport?._id) === toId(sport?._id),
	);

	return (
		sportEvents.find(
			(event) => toId(event.series?._id) === toId(state.selectedSeries?._id),
		) ?? sportEvents[0]
	);
};

const chooseEventForSeries = (
	state: FantasyState,
	series: Series,
): Event | undefined => {
	const seasonId = toId(state.selectedSeason?._id);
	const futureEvents = getFutureEventsForSeason(state.events, seasonId);
	const seriesEvents = futureEvents.filter(
		(event) => toId(event.series?._id) === toId(series?._id),
	);

	return (
		seriesEvents.find(
			(event) => toId(event.sport?._id) === toId(state.selectedSport?._id),
		) ?? seriesEvents[0]
	);
};

const chooseEventForDiscipline = (
	state: FantasyState,
	sport?: Sport,
	series?: Series,
): Event | undefined => {
	const seasonId = toId(state.selectedSeason?._id);
	const futureEvents = getFutureEventsForSeason(state.events, seasonId);

	return futureEvents.find((event) => {
		const matchesSport = sport?._id
			? toId(event.sport?._id) === toId(sport._id)
			: true;
		const matchesSeries = series?._id
			? toId(event.series?._id) === toId(series._id)
			: true;
		return matchesSport && matchesSeries;
	});
};

const applySelectedDiscipline = (
	state: FantasyState,
	sport?: Sport,
	series?: Series,
) => {
	const nextEvent = chooseEventForDiscipline(state, sport, series);
	if (nextEvent) {
		applySelectedEvent(state, nextEvent);
		return;
	}

	state.selectedSport = sport ?? initialSport;
	state.selectedSeries = series ?? initialSeries;
	state.selectedEvent = initialEvent;
	setFutureEventsForSelection(state);
};

const initialState: FantasyState = {
	sports: [],
	series: [],
	events: [],
	seasons: [],
	futureEvents: [],
	videos: [],
	riders: [],
	results: [],
	brandTeams: [],
	featuredRider: initialRider,
	featuredBrandTeam: initialBrandTeam,
	featuredRiderDescription: "2023 WORLD CHAMPION",
	featuredBrandTeamDescription: "NEW ROSTER AND DEVELOPMENT TEAM",
	selectedSport: initialSport,
	selectedSeries: initialSeries,
	selectedEvent: initialEvent,
	selectedSeason: initialSeason,
	selectedLeague: initialLeagueWithSportsAndSeries,
	selectedLeaderBoard: initialLeaderboard,
	selectedTeam: initialTeamComplete,
	eventLoading: false,
	leaderboardLoading: false,
	teamLoading: false,
};

const fantasySlice = createSlice({
	name: "fantasy",
	initialState,
	reducers: {
		setSports: (state, action: PayloadAction<Sport[]>) => {
			state.sports = action.payload;
		},
		setSeries: (state, action: PayloadAction<Series[]>) => {
			state.series = action.payload;
		},
		setSeasons: (state, action: PayloadAction<Season[]>) => {
			state.seasons = action.payload;
		},
		setEvents: (state, action: PayloadAction<Event[]>) => {
			state.events = action.payload;
			const selectedEventId = toId(state.selectedEvent?._id);
			const selectedEventStillExists = selectedEventId
				? action.payload.some((event) => toId(event._id) === selectedEventId)
				: true;

			if (!selectedEventStillExists) {
				const nextEvent =
					(state.selectedSport?._id
						? chooseEventForSport(state, state.selectedSport)
						: undefined) ??
					getFutureEventsForSeason(state.events, toId(state.selectedSeason?._id))[0];
				applySelectedEvent(state, nextEvent);
				return;
			}
			setFutureEventsForSelection(state);
		},
		updateFutureEvents: (state) => {
			setFutureEventsForSelection(state);
		},
		setVideos: (state, action: PayloadAction<Video[]>) => {
			state.videos = action.payload;
		},
		setFeaturedRider: (state, action: PayloadAction<Rider>) => {
			state.featuredRider = action.payload;
		},
		setFeaturedBrandTeam: (state, action: PayloadAction<BrandTeam>) => {
			state.featuredBrandTeam = action.payload;
		},
		setFeaturedRiderDescription: (state, action: PayloadAction<string>) => {
			state.featuredRiderDescription = action.payload;
		},
		setFeaturedBrandTeamDescription: (state, action: PayloadAction<string>) => {
			state.featuredBrandTeamDescription = action.payload;
		},
		setSelectedSeason: (state, action: PayloadAction<Season>) => {
			state.selectedSeason = action.payload;
			const seasonFutureEvents = getFutureEventsForSeason(
				state.events,
				toId(action.payload?._id),
			);
			if (state.selectedSport?._id) {
				const nextEvent = chooseEventForSport(state, state.selectedSport);
				if (nextEvent) {
					applySelectedEvent(state, nextEvent);
					return;
				}
			}
			if (seasonFutureEvents[0]) {
				applySelectedEvent(state, seasonFutureEvents[0]);
				return;
			}
			setFutureEventsForSelection(state);
		},
		setSelectedSeasonById: (state, action: PayloadAction<string>) => {
			const selectedSeason = state.seasons.find(
				(season) => season._id === action.payload,
			);
			if (selectedSeason) {
				state.selectedSeason = selectedSeason;
				const seasonFutureEvents = getFutureEventsForSeason(
					state.events,
					toId(selectedSeason?._id),
				);
				if (state.selectedSport?._id) {
					const nextEvent = chooseEventForSport(state, state.selectedSport);
					if (nextEvent) {
						applySelectedEvent(state, nextEvent);
						return;
					}
				}
				if (seasonFutureEvents[0]) {
					applySelectedEvent(state, seasonFutureEvents[0]);
					return;
				}
				setFutureEventsForSelection(state);
			}
		},
		setRiders: (state, action: PayloadAction<RiderWithSports[]>) => {
			state.riders = action.payload;
		},
		setBrandTeams: (state, action: PayloadAction<BrandTeam[]>) => {
			state.brandTeams = action.payload;
		},
		setResults: (state, action: PayloadAction<Result[]>) => {
			state.results = action.payload;
		},
		setSelectedSport: (state, action: PayloadAction<Sport>) => {
			const nextEvent = chooseEventForSport(state, action.payload);
			if (nextEvent) {
				applySelectedEvent(state, nextEvent);
				return;
			}
			state.selectedSport = action.payload;
			state.selectedSeries = initialSeries;
			state.selectedEvent = initialEvent;
			setFutureEventsForSelection(state);
		},
		setSelectedSeries: (state, action: PayloadAction<Series>) => {
			const nextEvent = chooseEventForSeries(state, action.payload);
			if (nextEvent) {
				applySelectedEvent(state, nextEvent);
				return;
			}
			state.selectedSeries = action.payload;
			state.selectedEvent = initialEvent;
			setFutureEventsForSelection(state);
		},
		setSelectedEvent: (state, action: PayloadAction<Event>) => {
			applySelectedEvent(state, action.payload);
		},
		setSelectedDiscipline: (
			state,
			action: PayloadAction<{ sport?: Sport; series?: Series }>,
		) => {
			applySelectedDiscipline(state, action.payload.sport, action.payload.series);
		},
		initializeEventContext: (
			state,
			action: PayloadAction<{ preferredSportId?: string } | undefined>,
		) => {
			if (
				state.selectedEvent?._id &&
				toId(state.selectedEvent.sport?._id) === toId(state.selectedSport?._id) &&
				toId(state.selectedEvent.series?._id) === toId(state.selectedSeries?._id)
			) {
				setFutureEventsForSelection(state);
				return;
			}

			const seasonId = toId(state.selectedSeason?._id);
			const futureEvents = getFutureEventsForSeason(state.events, seasonId);
			if (futureEvents.length === 0) {
				setFutureEventsForSelection(state);
				return;
			}

			const preferredSportId = action.payload?.preferredSportId;
			const selectedSportId = toId(state.selectedSport?._id);
			const downhillEvent = futureEvents.find((event) =>
				event.sport?.name?.toLowerCase() === "downhill",
			);
			const nextEvent =
				(preferredSportId
					? futureEvents.find(
							(event) => toId(event.sport?._id) === preferredSportId,
						)
					: undefined) ??
				(selectedSportId
					? futureEvents.find(
							(event) => toId(event.sport?._id) === selectedSportId,
						)
					: undefined) ??
				downhillEvent ??
				futureEvents[0];

			applySelectedEvent(state, nextEvent);
		},
		setSelectedLeague: (
			state,
			action: PayloadAction<LeagueWithSportsAndSeries>,
		) => {
			state.selectedLeague = action.payload;
		},
		setSelectedLeaderBoard: (state, action: PayloadAction<Leaderboard>) => {
			state.selectedLeaderBoard = action.payload;
		},
		setEventLoading: (state, action: PayloadAction<boolean>) => {
			state.eventLoading = action.payload;
		},
		setLeaderboardLoading: (state, action: PayloadAction<boolean>) => {
			state.leaderboardLoading = action.payload;
		},
		setSelectedTeam: (state, action: PayloadAction<TeamComplete>) => {
			state.selectedTeam = action.payload;
		},
		setTeamLoading: (state, action: PayloadAction<boolean>) => {
			state.teamLoading = action.payload;
		},
		fantasyLogout: (state) => {
			state.selectedTeam = initialTeamComplete;
		},
		resetFantasyState: () => initialState,
	},
});

export const {
	setSports,
	setSeries,
	setSeasons,
	setEvents,
	updateFutureEvents,
	setVideos,
	setFeaturedRider,
	setFeaturedBrandTeam,
	setFeaturedRiderDescription,
	setFeaturedBrandTeamDescription,
	setRiders,
	setResults,
	setBrandTeams,
	setSelectedSeason,
	setSelectedSeasonById,
	setSelectedSport,
	setSelectedSeries,
	setSelectedEvent,
	setSelectedDiscipline,
	initializeEventContext,
	setSelectedLeague,
	setSelectedLeaderBoard,
	setSelectedTeam,
	resetFantasyState,
	setEventLoading,
	setLeaderboardLoading,
	setTeamLoading,
	fantasyLogout,
} = fantasySlice.actions;

export default fantasySlice.reducer;
